import * as R from 'ramda';
import { pipe } from 'fp-ts/lib/function.js';
import { anyEquals } from '../arrays.js';
import { getOrdinalSuffix } from '../dates/index.js';
export const scoringSystem = [
    'Callback',
    'AudienceVote',
    'RelativePlacement',
];
export const contestFormats = ['Battle', 'Tournament', 'Spotlight'];
export const contestBrackets = ['Winners', 'Losers'];
export const contestRounds = [
    'Prelims',
    'Elimination',
    'QuarterFinals',
    'SemiFinals',
    'Finals',
];
export const contestStatusList = [
    'AcceptingVotes',
    'Completed',
    'Preparing',
    'Pending',
];
export const getActiveRound = (rounds) => pipe(rounds, sortContestRounds, 
// Remove complete stages
(c) => c.filter(({ status }) => status !== 'Completed'), 
// Get the first available stage in the list
(R.head));
export const isPendingPreviousRound = (rounds, round) => {
    const active = getActiveRound(rounds);
    if (!active)
        return false;
    if (ROUND_RANKS[round.type] > ROUND_RANKS[active.type])
        return true;
    return false;
};
export const getRoundStatus = (rounds, { status, type }) => {
    if (status === 'Completed')
        return 'Completed';
    if (status === 'AcceptingVotes')
        return 'AcceptingVotes';
    // Prelims is always the earliest
    if (type === 'Prelims')
        return status;
    const activeRound = getActiveRound(rounds);
    const rank = ROUND_RANKS[type];
    // There is only no current stage if every stage is completed
    if (!activeRound)
        return 'Completed';
    if (ROUND_RANKS[activeRound.type] >= rank)
        return status;
    return 'PendingCompletion';
};
export const ROUND_RANKS = pipe(contestRounds.map((stage, i) => [stage, i]), R.fromPairs);
export const sortContestRounds = (rounds) => rounds.sort((a, b) => ROUND_RANKS[a.type] - ROUND_RANKS[b.type]);
export const isValidContestState = (s) => anyEquals(s, contestRounds);
export const getContestStateFromParams = (params) => {
    const state = params.get('contestState');
    if (!isValidContestState(state))
        return undefined;
    return state;
};
export const CALLBACK_SCORE_MAP = {
    Y: 10,
    A1: 4.5,
    A2: 4.3,
    A3: 4.2,
};
export const translateVoteToText = (vote) => {
    if (vote === 'Y')
        return 'Yes';
    if (vote === 'A1')
        return '1st Alt';
    if (vote === 'A2')
        return '2nd Alt';
    if (vote === 'A3')
        return '3rd Alt';
    return '';
};
/** @warning WARNING:  This will only work if the array values are in order of ascending states */
export const getNextContestRound = (rounds) => {
    return pipe(rounds.filter(({ status }) => status === 'Pending'), sortContestRounds, (R.head));
};
/** @warning WARNING:  This will only work if the array values are in order of ascending states */
export const getPreviousContestRound = (rounds) => {
    return pipe(rounds.filter(({ status }) => status === 'Completed'), sortContestRounds, (R.head));
};
export const getJudgeAllowedScores = (round) => {
    return Object.entries(round.votesPerJudge)
        .filter(([_, amount]) => amount !== 0)
        .filter(([key, _]) => {
        if (key === 'Y')
            return true;
        if (key === 'A1')
            return true;
        if (key === 'A2')
            return true;
        if (key === 'A3')
            return true;
        return false;
    });
};
export const readableScore = (value, strategy) => {
    if (strategy === 'Callback') {
        const score = R.invertObj(CALLBACK_SCORE_MAP)[value];
        return translateVoteToText(score);
    }
    if (strategy === 'RelativePlacement')
        return `${value}${getOrdinalSuffix(value)} Place`;
    if (strategy === 'AudienceVote')
        return `${value} Points`;
    return value;
};
export const transformJudgesToTableSchema = (judges = [], system) => pipe(judges, R.map((judge) => [
    judge.name,
    ({ id }) => readableScore(judge.votes.find((vote) => vote.ticketId === id)?.value || 0, system),
]), R.fromPairs);
export const attachScoresToContestant = (contestant, judges) => {
    const votes = judges.flatMap(({ votes, name }) => [
        name,
        votes.find((v) => v.ticketId === contestant.id)?.value || 0,
    ]);
    // @ts-expect-error
    return R.fromPairs(votes);
};
export const isRoundAfter = (roundToCheck, comparison) => {
    const checkingRank = ROUND_RANKS[roundToCheck.type];
    const comparisonRank = ROUND_RANKS[comparison.type];
    return checkingRank > comparisonRank;
};
export const getRoundBefore = (round, rounds) => pipe(rounds, sortContestRounds, (sorted) => {
    const roundIndex = sorted.findIndex((r) => r.type === round.type);
    if (roundIndex <= 0)
        return undefined;
    return sorted[roundIndex - 1];
});
export const getRoundAfter = (round, rounds) => pipe(rounds, sortContestRounds, (sorted) => {
    const roundIndex = sorted.findIndex((r) => r.type === round.type);
    if (roundIndex === -1 || roundIndex >= sorted.length - 1)
        return undefined;
    return sorted[roundIndex + 1];
});
//# sourceMappingURL=scoring.helpers.js.map